import React, { useState } from 'react';
import { Plus, Trash2, Info, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

const RATING_OPTIONS = [10, 20, 30, 40, 50, 60, 70, 80, 90, 100];

// 2026 VA Compensation Rates (monthly)
const VA_RATES = {
  0: { alone: 0 },
  10: { alone: 175.51 },
  20: { alone: 347.14 },
  30: { alone: 537.94, withSpouse: 601.94, withSpouseAndChild: 649.94, withChild: 580.94 },
  40: { alone: 775.15, withSpouse: 862.15, withSpouseAndChild: 922.15, withChild: 823.15 },
  50: { alone: 1103.17, withSpouse: 1212.17, withSpouseAndChild: 1284.17, withChild: 1163.17 },
  60: { alone: 1395.79, withSpouse: 1527.79, withSpouseAndChild: 1611.79, withChild: 1467.79 },
  70: { alone: 1759.04, withSpouse: 1913.04, withSpouseAndChild: 2009.04, withChild: 1841.04 },
  80: { alone: 2044.65, withSpouse: 2221.65, withSpouseAndChild: 2329.65, withChild: 2138.65 },
  90: { alone: 2298.11, withSpouse: 2497.11, withSpouseAndChild: 2617.11, withChild: 2404.11 },
  100: { alone: 3823.89, withSpouse: 4028.69, withSpouseAndChild: 4160.69, withChild: 3933.69 },
};

function combineRatings(ratings) {
  if (ratings.length === 0) return 0;
  const sorted = [...ratings].sort((a, b) => b - a);
  let remaining = 100;
  for (const r of sorted) {
    remaining = remaining * (1 - r / 100);
  }
  const exact = 100 - remaining;
  return Math.round(exact / 10) * 10;
}

function applyBilateralFactor(conditions) {
  const bilateral = conditions.filter(c => c.bilateral);
  const nonBilateral = conditions.filter(c => !c.bilateral);

  if (bilateral.length < 2) {
    return conditions.map(c => c.rating);
  }

  // Combine bilateral conditions, add 10% bilateral factor
  let bilateralRemaining = 100;
  const bilateralRatings = bilateral.map(c => c.rating).sort((a, b) => b - a);
  for (const r of bilateralRatings) {
    bilateralRemaining = bilateralRemaining * (1 - r / 100);
  }
  const bilateralCombined = 100 - bilateralRemaining;
  const withFactor = bilateralCombined * 1.1; // 10% bilateral factor

  return [withFactor, ...nonBilateral.map(c => c.rating)];
}

const VADisabilityCalculator = () => {
  const [conditions, setConditions] = useState([{ id: 1, name: '', rating: 30, bilateral: false }]);
  const [dependents, setDependents] = useState('alone');
  const [nextId, setNextId] = useState(2);

  const addCondition = () => {
    setConditions([...conditions, { id: nextId, name: '', rating: 10, bilateral: false }]);
    setNextId(nextId + 1);
  };

  const removeCondition = (id) => {
    if (conditions.length > 1) {
      setConditions(conditions.filter(c => c.id !== id));
    }
  };

  const updateCondition = (id, field, value) => {
    setConditions(conditions.map(c => c.id === id ? { ...c, [field]: value } : c));
  };

  const ratingsToUse = applyBilateralFactor(conditions);
  const combinedRating = combineRatings(ratingsToUse);
  const exactCombined = (() => {
    const sorted = [...ratingsToUse].sort((a, b) => b - a);
    let remaining = 100;
    for (const r of sorted) {
      remaining = remaining * (1 - r / 100);
    }
    return (100 - remaining).toFixed(1);
  })();

  const rateData = VA_RATES[combinedRating] || VA_RATES[0];
  const monthlyPay = rateData[dependents] || rateData.alone || 0;

  return (
    <div className="space-y-6" data-testid="va-disability-calculator">
      {/* Conditions Input */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label className="text-sm font-semibold text-slate-800">Service-Connected Conditions</Label>
          <Button size="sm" variant="outline" onClick={addCondition} data-testid="add-condition-btn">
            <Plus className="w-3.5 h-3.5 mr-1" /> Add Condition
          </Button>
        </div>

        {conditions.map((condition) => (
          <div key={condition.id} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border">
            <Input
              placeholder="Condition name (e.g., Tinnitus)"
              value={condition.name}
              onChange={(e) => updateCondition(condition.id, 'name', e.target.value)}
              className="flex-1 h-9 text-sm"
              data-testid={`condition-name-${condition.id}`}
            />
            <select
              value={condition.rating}
              onChange={(e) => updateCondition(condition.id, 'rating', Number(e.target.value))}
              className="h-9 px-2 rounded-md border border-slate-200 text-sm bg-white"
              data-testid={`condition-rating-${condition.id}`}
            >
              {RATING_OPTIONS.map(r => (
                <option key={r} value={r}>{r}%</option>
              ))}
            </select>
            <label className="flex items-center gap-1.5 text-xs text-slate-600 whitespace-nowrap cursor-pointer">
              <input
                type="checkbox"
                checked={condition.bilateral}
                onChange={(e) => updateCondition(condition.id, 'bilateral', e.target.checked)}
                className="rounded"
              />
              Bilateral
            </label>
            {conditions.length > 1 && (
              <Button size="sm" variant="ghost" onClick={() => removeCondition(condition.id)} className="text-red-500 hover:text-red-700 h-8 w-8 p-0">
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            )}
          </div>
        ))}
      </div>

      {/* Dependents */}
      <div>
        <Label className="text-sm font-semibold text-slate-800 mb-2 block">Dependency Status</Label>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {[
            { value: 'alone', label: 'Veteran Alone' },
            { value: 'withSpouse', label: 'With Spouse' },
            { value: 'withChild', label: 'With Child' },
            { value: 'withSpouseAndChild', label: 'Spouse + Child' },
          ].map(opt => (
            <button
              key={opt.value}
              onClick={() => setDependents(opt.value)}
              data-testid={`dep-${opt.value}`}
              className={`p-2.5 rounded-lg border text-xs font-medium transition-all ${
                dependents === opt.value
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white text-slate-700 border-slate-200 hover:border-blue-300'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Results */}
      <Card className="border-blue-200 bg-gradient-to-br from-blue-50 to-white">
        <CardContent className="p-5">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center p-4 bg-white rounded-lg border">
              <p className="text-xs text-slate-500 mb-1">Combined Rating</p>
              <p className="text-3xl font-bold text-blue-700" data-testid="combined-rating">{combinedRating}%</p>
              <p className="text-[10px] text-slate-400 mt-1">Exact: {exactCombined}%</p>
            </div>
            <div className="text-center p-4 bg-white rounded-lg border">
              <p className="text-xs text-slate-500 mb-1">Monthly Compensation</p>
              <p className="text-3xl font-bold text-green-700" data-testid="monthly-pay">${monthlyPay.toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
              <p className="text-[10px] text-slate-400 mt-1">Tax-free</p>
            </div>
            <div className="text-center p-4 bg-white rounded-lg border">
              <p className="text-xs text-slate-500 mb-1">Annual Compensation</p>
              <p className="text-3xl font-bold text-green-700">${(monthlyPay * 12).toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
              <p className="text-[10px] text-slate-400 mt-1">Tax-free</p>
            </div>
          </div>

          {conditions.some(c => c.bilateral) && (
            <div className="mt-3 flex items-start gap-2 text-xs text-blue-700 bg-blue-100 p-2.5 rounded-lg">
              <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <span>Bilateral factor (10% bonus) applied to conditions affecting both sides of the body.</span>
            </div>
          )}

          {combinedRating < 30 && combinedRating > 0 && (
            <div className="mt-3 flex items-start gap-2 text-xs text-amber-700 bg-amber-50 p-2.5 rounded-lg">
              <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <span>Dependent rates only available at 30% or higher. Consider filing for secondary conditions to increase your rating.</span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* VA Math Explanation */}
      <details className="text-xs text-slate-600">
        <summary className="cursor-pointer font-medium text-slate-700 hover:text-blue-600">How VA math works</summary>
        <div className="mt-2 p-3 bg-slate-50 rounded-lg space-y-1">
          <p>The VA uses "combined ratings" math — not simple addition. Each subsequent rating is applied to the <em>remaining</em> healthy body percentage.</p>
          <p><strong>Example:</strong> 70% + 30% = 70% + (30% of remaining 30%) = 70% + 9% = 79% → rounds to 80%</p>
          <p>Ratings are rounded to the nearest 10%. The bilateral factor adds 10% to bilateral conditions before combining.</p>
        </div>
      </details>
    </div>
  );
};

export default VADisabilityCalculator;
